/**
 * v4.13.0 — Platform identity glyphs.
 *
 * Compact, color-faithful brand marks for the Fleet Ops surface. Each
 * glyph is a 16-default <svg> sized via the `size` prop and tinted
 * either with its native brand color (when `brand` is true) or with the
 * caller-supplied `color`. Lucide is used for non-brand destinations
 * (Reservations, Event, Direct Link, Landing, Custom).
 *
 * Single export: <PlatformIcon platform="google_reviews" size={14} brand />
 *
 * NOTE: no new dep added — every brand mark is hand-authored inline
 * SVG so the icon set is identical across light/dark themes and the
 * bundle stays tiny.
 */
import { Calendar, Ticket, Link2, Sparkles, Compass } from "lucide-react";

export const PLATFORM_COLORS = {
    google_reviews: "#ea4335",
    google: "#ea4335",
    facebook: "#1877f2",
    instagram: "#e1306c",
    tiktok: "#25f4ee",
    yelp: "#d32323",
    tripadvisor: "#34e0a1",
    trustpilot: "#00b67a",
    reservations: "#f59e0b",
    event: "#22d3ee",
    event_page: "#22d3ee",
    landing: "#10b981",
    direct: "#94a3b8",
    custom: "#94a3b8",
};

export const platformColor = (p) => PLATFORM_COLORS[p] || "#94a3b8";

// ───────────────────── brand glyphs (16×16 viewBox) ─────────────────────

const GoogleMark = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" fill={color || "#4285f4"} />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill={color || "#34a853"} />
        <path d="M5.84 14.12A6.93 6.93 0 0 1 5.5 12c0-.74.13-1.46.34-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z" fill={color || "#fbbc04"} />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill={color || "#ea4335"} />
    </svg>
);

const InstagramMark = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke={color || "#e1306c"} strokeWidth="1.8" />
        <circle cx="12" cy="12" r="4" stroke={color || "#e1306c"} strokeWidth="1.8" />
        <circle cx="17.5" cy="6.5" r="1.1" fill={color || "#e1306c"} />
    </svg>
);

const FacebookMark = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
        <path
            d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.5-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.45 2.91h-2.33V22c4.78-.79 8.44-4.94 8.44-9.94z"
            fill={color || "#1877f2"}
        />
    </svg>
);

const TikTokMark = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
        <path
            d="M16.5 2.4c.13 1.46.8 2.85 1.89 3.85.94.88 2.1 1.4 3.31 1.5v3.4c-1.69.04-3.34-.5-4.71-1.55v6.5c0 4.05-3.32 7.34-7.4 7.34S2.2 20.15 2.2 16.1c0-4.05 3.32-7.33 7.4-7.33.45 0 .89.04 1.32.13v3.55a3.84 3.84 0 0 0-1.32-.24c-2.16 0-3.91 1.74-3.91 3.89 0 2.15 1.75 3.89 3.91 3.89s3.91-1.74 3.91-3.89V2.4h2.99z"
            fill={color || "#25f4ee"}
        />
    </svg>
);

const YelpMark = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
        <path
            d="M13.5 2.2c-.4-.2-3.3.5-4.3 1-.6.3-.8.7-.7 1.4l1 7.2c.1.6.6.9 1.1.7l4.3-1.7c.5-.2.7-.7.4-1.2L13.5 2.2zM10.6 14.6 6 17.1c-.5.3-.6 1-.1 1.4l3.2 2.9c.5.5 1.3.2 1.5-.5l1.3-4.6c.2-.7-.5-1.4-1.3-.9zm7.6-2.2-3.6-2.8c-.6-.5-1.5 0-1.4.7l.4 5.4c0 .8.9 1.2 1.5.7l3.2-2.5c.5-.4.5-1.1-.1-1.5zm-1 6.9-3.7-2.5c-.7-.5-1.6.1-1.4.9l1.1 4.6c.2.7.9 1 1.4.7l3-1.8c.6-.4.7-1.4-.4-1.9z"
            fill={color || "#d32323"}
        />
    </svg>
);

const TripAdvisorMark = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
        <circle cx="12" cy="13" r="9.5" stroke={color || "#34e0a1"} strokeWidth="1.6" fill="none" />
        <circle cx="8.5" cy="13" r="2.4" fill={color || "#34e0a1"} />
        <circle cx="15.5" cy="13" r="2.4" fill={color || "#34e0a1"} />
        <circle cx="8.5" cy="13" r="0.7" fill="#0f172a" />
        <circle cx="15.5" cy="13" r="0.7" fill="#0f172a" />
    </svg>
);

const TrustpilotMark = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
        <path d="M12 2l2.6 6.2 6.7.5-5.1 4.4 1.6 6.5L12 15.9 6.2 19.6l1.6-6.5L2.7 8.7l6.7-.5L12 2z" fill={color || "#00b67a"} />
    </svg>
);

// ───────────────────── exported component ─────────────────────

export default function PlatformIcon({ platform, size = 14, brand = true, className }) {
    const p = (platform || "").toLowerCase();
    const tint = brand ? undefined : "currentColor";

    if (p === "google_reviews" || p === "google") {
        return <span className={className} style={{ display: "inline-flex" }}>
            <GoogleMark size={size} color={tint} />
        </span>;
    }
    if (p === "instagram") return <span className={className}><InstagramMark size={size} color={tint} /></span>;
    if (p === "facebook")  return <span className={className}><FacebookMark size={size} color={tint} /></span>;
    if (p === "tiktok")    return <span className={className}><TikTokMark size={size} color={tint} /></span>;
    if (p === "yelp")      return <span className={className}><YelpMark size={size} color={tint} /></span>;
    if (p === "tripadvisor") return <span className={className}><TripAdvisorMark size={size} color={tint} /></span>;
    if (p === "trustpilot")  return <span className={className}><TrustpilotMark size={size} color={tint} /></span>;
    if (p === "reservations") return <Calendar size={size} className={className} color={brand ? platformColor("reservations") : undefined} strokeWidth={1.85} />;
    if (p === "event_page" || p === "event") return <Ticket size={size} className={className} color={brand ? platformColor("event") : undefined} strokeWidth={1.85} />;
    if (p === "landing") return <Sparkles size={size} className={className} color={brand ? platformColor("landing") : undefined} strokeWidth={1.85} />;
    if (p === "custom" || p === "direct") return <Link2 size={size} className={className} color={brand ? platformColor("direct") : undefined} strokeWidth={1.85} />;
    return <Compass size={size} className={className} color="#94a3b8" strokeWidth={1.85} />;
}
